import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { createChart, destroyChartAtIndex, getData } from "./helpers.js";
import "./polyfills";

const INIT_QUERY_PARAM = 'bd_chart_gas_init';
const YEAR_QUERY_PARAM = 'bd_chart_gas_ele_year';
const COMMODITY_QUERY_PARAM = 'bd_chart_gas_ele_commodity';
const DISTRIBUTOR_QUERY_PARAM = 'bd_chart_gas_ele_distributor';
const TARIFF_QUERY_PARAM = 'bd_chart_gas_ele_tariff';

const App = (props) => {

    const {dataset, chartIndex} = props;

    const [alloweCommodities, setAllowedCommodities] = useState(dataset.bdchartallowedcommodities.split(',').map(item =>  item.trim())); 
    const [year, setYear] = useState(null);
    const [commodity, setCommodity] = useState(dataset.bdchartdefaultcommodity);
    const [distributors, setDistributors] = useState(null);
    const [distributorsOpened, setDistributorsOpened] = useState(false);
    const [tariffsOpened, setTariffsOpened] = useState(false);
    const [distributor, setDistributor] = useState(null);
    const [tariff, setTariff] = useState(null);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [allowedYears, setAllowedYears] = useState([]);
    const [expandDisclaimer, setExpandDisclaimer] = useState(false);
    const [error, setError] = useState(false);
    const canvasElement = useRef(null);

    const title = dataset.bdcharttitle;
    const showDisclaimer = dataset.bdchartshowdisclaimer === "true";
    const queryParamsSuffix = dataset.bdchartqueryparamssuffix || '';

    const getInitData = () => {
        if(!dataset.bdchartdataurl) return;
        setLoading(true);
        getData(`${dataset.bdchartdataurl}?${INIT_QUERY_PARAM}${queryParamsSuffix}=1`,  
            (e, oReq) => {

                try {
                    const result = JSON.parse(oReq.response);
    
                    setYear(result.default_year || new Date().getFullYear());
    
                    const distributors = result.distributors;
                    let distributor = '';
                    for (let i = 0; i < distributors[commodity].length; i++) {
                        const hasActiveTariff = distributors[commodity][i].tariffs.find(tariff =>  tariff.active);
    
                        if (hasActiveTariff) {
                            distributor = distributors[commodity][i];
                            const {active, ...tariffObject} = hasActiveTariff;
                            setTariff(tariffObject);
                            break;
                        }
                    }
                    setDistributor(distributor || distributors[commodity][0]);
                    setDistributors(distributors);
                } catch (err) {
                    setError('Nastala chyba při načítání dat grafu');
                    destroyChartAtIndex(chartIndex);
                } finally {
                    setLoading(false);
                }
            }, () => {
                setLoading(false);          
            }
        );
    }

    const getDataAndRenderChart = () => {
        if(!dataset.bdchartdataurl || (!tariff && commodity === 'ele') || !commodity || !year || !distributor) return;
        setLoading(true);
        setData(null);
        getData(`${dataset.bdchartdataurl}?${YEAR_QUERY_PARAM}${queryParamsSuffix}=${year}&${COMMODITY_QUERY_PARAM}${queryParamsSuffix}=${commodity}&${DISTRIBUTOR_QUERY_PARAM}${queryParamsSuffix}=${distributor.id}&${TARIFF_QUERY_PARAM}${queryParamsSuffix}=${tariff && tariff.id || 'undefined'}`, 
            (e, oReq) => {
                try {
                    const parsedResponse = JSON.parse(oReq.response);
                    createChart(canvasElement.current, parsedResponse, dataset, chartIndex);
                    setAllowedYears(parsedResponse.visibleYears ? parsedResponse.visibleYears.map(year => parseInt(year, 10)) : []);
                    setData(!!parsedResponse.data);
                    if (error) {
                        setError('');
                    }
                } catch (err) {
                    setError('Nastala chyba při načítání dat grafu');
                    destroyChartAtIndex(chartIndex);
                } finally {
                    setLoading(false);
                }
            }, () => {
                setData(false);
                setLoading(false);          
            }
        );
    }

    useEffect(() => {
        getInitData();
    }, []);

    useEffect(() => {
        getDataAndRenderChart();
    }, [year, commodity, distributor, tariff]);


    return <div className={`bd-chart-gas-ele-inner ${data ? 'data-ok' : loading || data === null || error ? '' : 'data-nok'}${loading ? ' loading' : ''}`}>
        {/* heading */}

        <div className="bd-chart-gas-ele-heading">

            {/* rok */}

            <div className="bd-chart-gas-ele-heading-year">{title} <strong>{year}</strong></div>

            {/* komodita */}

            <div className="bd-chart-gas-ele-heading-commodity">

                {alloweCommodities.find(item => item === 'ele') && <span className={`bd-chart-gas-ele-commodity-ele ${commodity === 'ele' ? 'active' : 'not-active'}`} onClick={() => {
                    setCommodity('ele');
                    setTariffsOpened(false);
                    setDistributorsOpened(false);
                    if (distributors && distributors['ele']) {
                        setDistributor(distributors['ele'][0]);
                        setTariff(distributors['ele'][0].tariffs ? {...distributors['ele'][0].tariffs[0]} : null);
                    }
                }}>Elektřina</span>}

                {alloweCommodities.find(item => item === 'gas') && <span className={`bd-chart-gas-ele-commodity-gas ${commodity === 'gas' ? 'active' : 'not-active'}`} onClick={() => {
                    setCommodity('gas');
                    setTariffsOpened(false);
                    setDistributorsOpened(false);
                    if (distributors && distributors['gas']) {
                        setDistributor(distributors['gas'][0]);
                        setTariff(distributors['gas'][0].tariffs ? {...distributors['gas'][0].tariffs[0]} : null);
                    }
                }}>Plyn</span>}
                
            </div>

            {/* distributor */}

            <div className="bd-chart-gas-ele-heading-distributor">
                {/* <div className="bd-chart-gas-ele-heading-distributor-switcher" onClick={() => setDistributorsOpened(!distributorsOpened)}>
                <span>{`Distribuční oblast:`} <strong>{distributor ? distributor.name : null} &#9660;</strong></span>
                    {distributors && distributorsOpened && <div className="bd-chart-gas-ele-heading-distributor-menu">
                        <ul>
                            {distributors[commodity].map((distributor, index) => <li key={index} onClick={() => {
                                setDistributor(distributor);
                                setTariff({...distributor.tariffs[0]});
                                setTariffsOpened(false);
                            }}>{distributor.name}</li>)}
                        </ul>
                    </div>}
                </div> */}

                {/* sazby */}

                {distributor && distributor.tariffs && !!distributor.tariffs.length && <div className="bd-chart-gas-ele-heading-distributor-tariff-switcher" onClick={() => setTariffsOpened(!tariffsOpened)}>
                    <span>{`Distribuční sazba:`} <strong>{tariff ? tariff.name : null} &#9660;</strong></span>
                    {distributor && tariffsOpened && <div className="bd-chart-gas-ele-heading-distributor-tariff-menu">
                        <ul>
                            {distributor.tariffs.map((tariff, index) => <li key={index} onClick={() => {
                                setTariff(tariff);
                                setDistributorsOpened(false);
                            }}>{tariff.name}</li>)}
                        </ul>
                    </div>}
                </div>}
            </div>

        </div>

        {/*  prev / next */}

        {year > Math.min(...allowedYears) && <span className="bd-chart-gas-ele-prev" onClick={() => setYear(year - 1)} />}
        {year < Math.max(...allowedYears) && <span className="bd-chart-gas-ele-next" onClick={() => setYear(year + 1)} />}

        {error && !loading ? <div className="bd-chart-gas-ele-error">{error}</div> : null}

        {/* canvas */}

        <div className="bd-chart-gas-ele-canvas-wrapper">
            <canvas className="bd-chart-gas-ele-canvas" ref={canvasElement} />
            <div className="bd-chart-gas-ele-no-data">Data momentálně nejsou k dispozici</div>
        </div>
        {showDisclaimer ? <div className="bd-chart-gas-ele-disclaimer">
            <p><i/>V Grafu porovnáváme naší tržní cenu s nejběžnější cenou v domácnostech, tj. s cenou základních produktových řad tzv. dominantních dodavatelů... <a href="#" onClick={(event) => {
                event.preventDefault();
                setExpandDisclaimer(!expandDisclaimer);
            }} title="Zjistit více o grafu">Zjistit více o grafu</a>
            </p>
            <p><strong>S ohledem na bezprecedentní situaci na energetickém trhu jsme dočasně přestali od 1. 1. 2022 provádět srovnání s dominantními dodavateli</strong></p>
            {expandDisclaimer && <div className="bd-chart-gas-ele-disclaimer-popup">
                <span className="bd-chart-gas-ele-disclaimer-popup-close" dangerouslySetInnerHTML={{__html: "&times"}} onClick={() => setExpandDisclaimer(false)} />
                <div>
                <p>V Grafu porovnáváme naší tržní cenu s nejběžnější cenou v domácnostech, tj. s cenou základních produktových řad tzv. dominantních dodavatelů (ti, co na „svém“ území provozují i distribuční síť ). Pokud jste nezměnili dodavatele, nebo si nevyjednali lepší tarif, tak tuto cenu pravděpodobně platíte.</p>
                <p>Pro elektřinu jsou to <strong>ČEZ Prodej, s.r.o.</strong> (produktová řada Elektřina na dobu neurčitou), <strong>Pražská energetika, a.s.</strong> (produktová řada KOMFORT) a <strong>E.ON Energie, a.s.</strong> (produktová řada Elektřina), vždy ve své distribuční oblasti.</p>
                <p>Pro plyn jsou to innogy <strong>Energie, s.r.o.</strong> (produktová řada plyn Standard), <strong>Pražská plynárenská, a.s.</strong> (produktová řada Standard), a <strong>E.ON Energie, a.s.</strong> (produktová řada Standard plyn) , vždy ve své distribuční oblasti.</p>
                <p>Porovnání zahrnuje pouze cenu energie, nikoliv stálý plat a regulovanou složku ceny. Zobrazené ceny jsou včetně DPH a zaokrouhleny na celé koruny nahoru. </p>
                <p>Pro přepočet dvoutarifních sazeb jsou použity tyto poměry spotřeb <strong>D25d, D26d (VT=46 %, NT=54 %); D35d (VT=15 %, NT=85 %); D45d, D55d, D56d, D57d, D61d (VT=6 %, NT=94 %)</strong>.</p>
                <p><strong>S ohledem na bezprecedentní situaci na energetickém trhu jsme dočasně přestali od 1. 1. 2022 provádět srovnání s dominantními dodavateli.</strong></p>
                </div>
            </div>}
        </div> : null}
    </div>;
}

const destinations = document.getElementsByClassName('bd-chart-gas-ele-wrapper');

for(let i = 0; i < destinations.length; i++) {
    ReactDOM.render(<App chartIndex={`index_${i}`} dataset={destinations[i].dataset} />, destinations[i]);
}
